/* ============================================================================
   VILLAINS TCG — Carousel enhancer (cst-carousel.js)
   Progressive enhancement for `.cst-carousel` scroll-snap rows: pagination dots,
   in-view autoplay (pauses on hover / focus / drag / user-scroll, resumes after),
   mouse drag-to-scroll, and active-dot tracking. MOBILE-ONLY — matches the CSS
   carousel breakpoint (≤749px); desktop keeps its grid untouched.

   The swipe itself stays native (scroll-snap), so this is pure enhancement:
   with no JS the rows still swipe. Movement is compositor-driven scrolling (no
   layout thrash). Autoplay honors prefers-reduced-motion and off-screen state.
   ========================================================================== */
(() => {
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const AUTOPLAY_MS = 5000;
  const RESUME_MS = 4500;
  const registry = new WeakMap();

  class CstCarousel {
    constructor(el) {
      this.el = el;
      this.items = Array.prototype.filter.call(el.children, (n) => n.nodeType === 1);
      this.index = 0;
      this.dragging = false;
      this.visible = false;
      this.timer = null;
      this.resumeT = null;
      this.rafPending = false;

      const dots = document.createElement('div');
      dots.className = 'cst-carousel__dots';
      dots.setAttribute('role', 'group');
      dots.setAttribute('aria-label', 'Slide navigation');
      dots.hidden = true;
      this.dotEls = this.items.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cst-carousel__dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        b.addEventListener('click', () => this.go(i, true));
        dots.appendChild(b);
        return b;
      });
      el.insertAdjacentElement('afterend', dots);
      this.dots = dots;

      el.addEventListener('scroll', this.onScroll, { passive: true });
      el.addEventListener('pointerenter', this.pause);
      el.addEventListener('pointerleave', this.resumeSoon);
      el.addEventListener('focusin', this.pause);
      el.addEventListener('focusout', (e) => { if (!el.contains(e.relatedTarget)) this.resumeSoon(); });
      el.addEventListener('pointerdown', this.onDown);
      window.addEventListener('pointermove', this.onMove);
      window.addEventListener('pointerup', this.onUp);
      window.addEventListener('pointercancel', this.onUp);

      // only autoplay while on screen
      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.resumeSoon(); else this.pause();
      }, { threshold: 0.4 });
      this.io.observe(el);

      this.paint(0);
      this.sync();
    }

    stride() {
      return this.items.length > 1 ? (this.items[1].offsetLeft - this.items[0].offsetLeft) || 1 : 1;
    }

    onScroll = () => {
      if (this.rafPending) return;
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.rafPending = false;
        const el = this.el;
        let i = Math.round(el.scrollLeft / this.stride());
        if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 2) i = this.items.length - 1;
        this.index = Math.max(0, Math.min(this.items.length - 1, i));
        this.paint(this.index);
      });
    };

    paint(i) {
      this.dotEls.forEach((d, x) => {
        const on = x === i;
        d.classList.toggle('is-on', on);
        if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
    }

    go(i, user) {
      const n = this.items.length;
      i = ((i % n) + n) % n;
      this.el.scrollTo({ left: this.items[i].offsetLeft - this.items[0].offsetLeft, behavior: REDUCE.matches ? 'auto' : 'smooth' });
      this.index = i;
      this.paint(i);
      if (user) this.resumeSoon();
    }
    next() { this.go(this.index + 1); }

    /* autoplay */
    play = () => {
      this.stopTimer();
      if (!MOBILE.matches || REDUCE.matches || !this.visible || this.items.length < 2) return;
      this.timer = setInterval(() => { if (!this.dragging) this.next(); }, AUTOPLAY_MS);
    };
    stopTimer() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
    pause = () => { this.stopTimer(); clearTimeout(this.resumeT); };
    resumeSoon = () => { clearTimeout(this.resumeT); this.resumeT = setTimeout(this.play, RESUME_MS); };

    /* mouse drag-to-scroll (touch keeps native scroll) */
    onDown = (e) => {
      if (!MOBILE.matches || e.pointerType !== 'mouse') return;
      this.dragging = true;
      this.dragX = e.clientX;
      this.dragScroll = this.el.scrollLeft;
      this.el.classList.add('is-dragging');
      this.pause();
    };
    onMove = (e) => {
      if (!this.dragging) return;
      this.el.scrollLeft = this.dragScroll - (e.clientX - this.dragX);
    };
    onUp = () => {
      if (!this.dragging) return;
      this.dragging = false;
      this.el.classList.remove('is-dragging');
      this.go(this.index);
      this.resumeSoon();
    };

    sync = () => {
      // Only enhance to a live carousel when the row actually overflows its
      // viewport. A row whose items all fit (e.g. 2 category tiles in a 2-up
      // grid) needs no dots / grab-cursor / autoplay — that would be a dead
      // affordance that can't scroll.
      const overflows = this.el.scrollWidth - this.el.clientWidth > 4;
      if (MOBILE.matches && this.items.length > 1 && overflows) {
        this.dots.hidden = false;
        this.el.classList.add('cst-carousel--live');
        this.resumeSoon();
      } else {
        this.dots.hidden = true;
        this.el.classList.remove('cst-carousel--live', 'is-dragging');
        this.pause();
        this.el.scrollLeft = 0;
        this.index = 0;
        this.paint(0);
      }
    };
  }

  const scan = () => {
    document.querySelectorAll('.cst-carousel').forEach((el) => {
      if (registry.has(el) || el.children.length < 2) return;
      registry.set(el, new CstCarousel(el));
    });
  };
  const syncAll = () => document.querySelectorAll('.cst-carousel').forEach((el) => { const c = registry.get(el); if (c) c.sync(); });

  if (document.readyState !== 'loading') scan(); else document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('shopify:section:load', scan);
  MOBILE.addEventListener('change', syncAll);
})();
